/**
 * aramSpawnMeshes — AR-R21 / FID-MAP-03
 * Murder Bridge spawn / buy-pad: deck + shop-radius disc (+ outline ring).
 */

import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  RingGeometry
} from 'three';
import { ARAM_CONFIG } from '../../domain/map/aramRules.js';
import { ARAM_STRUCTURE_ALONG } from '../../domain/map/aramLayout.js';
import { ARAM_BRIDGE_WIDTH } from '../../domain/map/aramScale.js';

export const SPAWN_PAD_CONFIG = {
  size: ARAM_BRIDGE_WIDTH * 0.55,
  height: 0.7,
  distance: ARAM_STRUCTURE_ALONG.spawn
};

/** Shop buy-zone disc readability (FID-MAP-03). Radius always === ARAM_CONFIG.spawnRadius. */
export const SHOP_ZONE_DISC = Object.freeze({
  height: 0.1,
  opacity: 0.7,
  emissiveIntensity: 0.7,
  outlineWidth: 0.35
});

const DECK_COLORS = { blue: 0x1e3a5f, red: 0x5f1e1e };
const ZONE_COLORS = { blue: 0x38bdf8, red: 0xf87171 };

function resolveClasses(THREE) {
  return {
    GroupCls: THREE?.Group || Group,
    BoxGeo: THREE?.BoxGeometry || BoxGeometry,
    CylinderGeo: THREE?.CylinderGeometry || CylinderGeometry,
    RingGeo: THREE?.RingGeometry || RingGeometry,
    MeshMat: THREE?.MeshStandardMaterial || MeshStandardMaterial,
    MeshCls: THREE?.Mesh || Mesh
  };
}

function buildDeckMesh(THREE, team) {
  const { BoxGeo, MeshMat, MeshCls } = resolveClasses(THREE);
  const { size: s, height: h } = SPAWN_PAD_CONFIG;
  const geo = new BoxGeo(s, h, s);
  const mat = new MeshMat({
    color: DECK_COLORS[team] || DECK_COLORS.blue,
    roughness: 0.65,
    emissive: DECK_COLORS[team] || DECK_COLORS.blue,
    emissiveIntensity: 0.12
  });
  const mesh = new MeshCls(geo, mat);
  mesh.name = `aram-spawn-deck-${team}`;
  mesh.position.set(0, h / 2, 0);
  return mesh;
}

function buildShopZoneMesh(THREE, team) {
  const { CylinderGeo, MeshMat, MeshCls } = resolveClasses(THREE);
  const { height: h } = SPAWN_PAD_CONFIG;
  const r = ARAM_CONFIG.spawnRadius;
  const geo = new CylinderGeo(r, r, SHOP_ZONE_DISC.height, 24);
  const mat = new MeshMat({
    color: ZONE_COLORS[team] || ZONE_COLORS.blue,
    emissive: ZONE_COLORS[team] || ZONE_COLORS.blue,
    emissiveIntensity: SHOP_ZONE_DISC.emissiveIntensity,
    transparent: true,
    opacity: SHOP_ZONE_DISC.opacity,
    roughness: 0.35
  });
  const mesh = new MeshCls(geo, mat);
  mesh.name = `aram-shop-zone-${team}`;
  mesh.position.set(0, h + 0.05, 0);
  return mesh;
}

function buildShopZoneOutline(THREE, team) {
  const { RingGeo, MeshMat, MeshCls } = resolveClasses(THREE);
  const { height: h } = SPAWN_PAD_CONFIG;
  const r = ARAM_CONFIG.spawnRadius;
  const geo = new RingGeo(Math.max(0.05, r - SHOP_ZONE_DISC.outlineWidth), r + 0.05, 32);
  const mat = new MeshMat({
    color: ZONE_COLORS[team] || ZONE_COLORS.blue,
    emissive: ZONE_COLORS[team] || ZONE_COLORS.blue,
    emissiveIntensity: 0.95,
    transparent: true,
    opacity: 0.9,
    roughness: 0.3,
    side: 2
  });
  const mesh = new MeshCls(geo, mat);
  mesh.name = `aram-shop-zone-outline-${team}`;
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(0, h + 0.12, 0);
  return mesh;
}

/** Raised buy-pad group: deck + shop-radius disc/outline (matches canShopInAram). */
export function buildSpawnPlatform(THREE, team) {
  const { GroupCls } = resolveClasses(THREE);
  const group = new GroupCls();
  group.name = `aram-spawn-${team}`;
  group.add(buildDeckMesh(THREE, team));
  group.add(buildShopZoneMesh(THREE, team));
  group.add(buildShopZoneOutline(THREE, team));
  const z = team === 'blue' ? SPAWN_PAD_CONFIG.distance : -SPAWN_PAD_CONFIG.distance;
  group.position.set(0, 0, z);
  return group;
}
